import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { FiTrendingUp, FiBarChart2, FiPieChart, FiActivity } = FiIcons;

const Analytics = () => {
  // Generate sample data for the past 30 days
  const generateTimeData = () => {
    const data = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push([
        format(date, 'MMM dd'),
        Number((Math.random() * 0.5 + 0.5).toFixed(3))
      ]);
    }
    return data;
  };

  const priceData = generateTimeData();

  // Price Chart Options
  const priceChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const [param] = params;
        return `${param.name}<br/>Price: $${param.value[1]}`;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: priceData.map(item => item[0]),
      axisLine: { lineStyle: { color: '#6b7280' } }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#6b7280' } },
      splitLine: { lineStyle: { color: '#e5e7eb' } }
    },
    series: [{
      name: 'Price',
      type: 'line',
      data: priceData,
      smooth: true,
      showSymbol: false,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(236, 72, 153, 0.3)'
          }, {
            offset: 1,
            color: 'rgba(236, 72, 153, 0)'
          }]
        }
      },
      lineStyle: {
        width: 3,
        color: '#ec4899'
      }
    }]
  };

  // Volume Distribution Chart
  const volumeChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}%'
    },
    series: [{
      name: 'Volume Distribution',
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 20,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: [
        { value: 40, name: 'DEX Trading' },
        { value: 30, name: 'CEX Trading' },
        { value: 20, name: 'Liquidity Pools' },
        { value: 10, name: 'Others' }
      ],
      color: ['#ec4899', '#8b5cf6', '#06b6d4', '#10b981']
    }]
  };

  return (
    <section id="analytics" className="py-20 px-4">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              Analytics
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Track PinkZilla's performance with real-time analytics and insights
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Key Metrics */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-pink-100">
                <SafeIcon icon={FiTrendingUp} className="text-2xl text-pink-600 mb-2" />
                <div className="text-2xl font-bold text-gray-800">$0.00842</div>
                <div className="text-sm text-gray-600">Current Price</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100">
                <SafeIcon icon={FiBarChart2} className="text-2xl text-purple-600 mb-2" />
                <div className="text-2xl font-bold text-gray-800">$2.5M</div>
                <div className="text-sm text-gray-600">Market Cap</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100">
                <SafeIcon icon={FiPieChart} className="text-2xl text-blue-600 mb-2" />
                <div className="text-2xl font-bold text-gray-800">50K+</div>
                <div className="text-sm text-gray-600">Holders</div>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100">
                <SafeIcon icon={FiActivity} className="text-2xl text-green-600 mb-2" />
                <div className="text-2xl font-bold text-gray-800">$500K</div>
                <div className="text-sm text-gray-600">24h Volume</div>
              </div>
            </div>
          </motion.div>

          {/* Volume Distribution Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-4">Volume Distribution</h3>
            <ReactECharts option={volumeChartOption} style={{ height: '300px' }} />
          </motion.div>
        </div>

        {/* Price Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-2xl p-6 shadow-lg"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Price History</h3>
          <ReactECharts option={priceChartOption} style={{ height: '400px' }} />
        </motion.div>
      </div>
    </section>
  );
};

export default Analytics;